import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteConfirmDialog } from '@components/DeleteConfirmDialog';
import { StickyHeaderLayout } from '@components/StickyHeaderLayout';
import { useBulkClaimX12Download, useBulkEraDownload, useClaimSubmission, useClaimSubmit } from '@features/billing/store';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import { Button, CircularProgress, Container, Paper, Stack, Tab, Tabs } from '@mui/material';
import type { ClaimListItem, OrgBillingEncounterResponse, RemittanceListItem } from '@store/generated/billing';
import { useSnackbar } from 'notistack';

import { StatementsHistory } from './statements/StatementsHistory';
import { StatementsQueue } from './statements/StatementsQueue';
import { BillingFilters } from './BillingFilters';
import { ClaimQueueChips } from './ClaimQueueChips';
import { ClaimsList } from './claims';
import { EncounterQueueChips } from './EncounterQueueChips';
import { BillingEncountersList } from './encounters';
import { useQueryParams } from './hooks';
import { RemittanceQueueChips } from './RemittanceQueueChips';
import { RemittancesList } from './remittances';
import type { TabType } from './types';


export const BillingLanding: React.FC = () => {
  const { params, updateParams, clearFilters, hasActiveFilters } = useQueryParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { submitClaim, deleteClaim } = useClaimSubmission();
  const { submitClaim: submitClaimToOffice } = useClaimSubmit();
  // Track selected encounters
  const [selectedEncounters, setSelectedEncounters] = useState<OrgBillingEncounterResponse[]>([]);
  const [isCreatingClaims, setIsCreatingClaims] = useState(false);
  const [isCreatingSuperbill, setIsCreatingSuperbill] = useState(false);

  // Track selected claims for deletion and submission
  const [selectedClaims, setSelectedClaims] = useState<ClaimListItem[]>([]);
  const [isDeletingClaims, setIsDeletingClaims] = useState(false);
  const [isSubmittingClaims, setIsSubmittingClaims] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Track selected remittances for download
  const [selectedRemittances, setSelectedRemittances] = useState<RemittanceListItem[]>([]);

  // X12 download hooks
  const { downloadBulkEra, isDownloading: isDownloadingEras } = useBulkEraDownload();
  const { downloadBulkClaims, isDownloading: isDownloadingClaims } = useBulkClaimX12Download();

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabType) => {
    updateParams({
      tab: newValue,
      status: undefined,
      // Keep providers across tab switches since both encounters and claims use it
      hasClaim: undefined,
      readyToBill: undefined,
      sort: undefined,
      order: undefined,
      // Clear remittance-specific params when switching tabs
      payerName: undefined,
      paymentMethod: undefined,
      queue: undefined,
    }, true);
  };

  const handleFilterChange = useCallback(
    (filters: {
      patient?: string;
      from?: string;
      to?: string;
      status?: string;
      providers?: string;
      payerUuid?: string;
      hasClaim?: string;
      readyToBill?: string;
      payerName?: string;
      paymentMethod?: string;
    }) => {
      updateParams(filters, true);
    },
    [updateParams],
  );

  const handleSort = useCallback(
    (field: string) => {
      const newOrder = params.sort === field && params.order === 'asc' ? 'desc' : 'asc';
      updateParams({ sort: field, order: newOrder }, true);
    },
    [params.sort, params.order, updateParams],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      updateParams({ page });
    },
    [updateParams],
  );

  const handlePerPageChange = useCallback(
    (perPage: number) => {
      updateParams({ perPage, page: 1 });
    },
    [updateParams],
  );

  // Clear selection when tab changes
  useEffect(() => {
    setSelectedEncounters([]);
    setSelectedClaims([]);
  }, [params.tab]);

  // Handle creating individual claims from selected encounters
  const handleCreateClaims = useCallback(async () => {
    if (selectedEncounters.length === 0 || isCreatingClaims) return;

    setIsCreatingClaims(true);
    const billableEncounters = selectedEncounters
      .filter((enc): enc is OrgBillingEncounterResponse & { patient_id: string } =>
        Boolean(enc.billable && !enc.has_claim && enc.patient_id)
      );
    const results = await Promise.allSettled(
      billableEncounters.map(enc =>
        submitClaim({
          encounter_ids: [enc.encounter_id],
          patient_id: enc.patient_id,
        })
      )
    );

    const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
    const failureCount = results.length - successCount;

    if (successCount > 0) {
      enqueueSnackbar(
        `Successfully created ${successCount} claim${successCount > 1 ? 's' : ''}`,
        { variant: 'success' }
      );

      if (successCount === 1) {
        const successResult = results.find(r => r.status === 'fulfilled' && r.value.success);
        if (successResult?.status === 'fulfilled' && successResult.value.data) {
          navigate(`/billing/claim/${successResult.value.data.id}/edit`);
        }
      } else {
        updateParams({ tab: 'claims' }, true);
      }

      setSelectedEncounters([]);
    }

    if (failureCount > 0) {
      enqueueSnackbar(
        `Failed to create ${failureCount} claim${failureCount > 1 ? 's' : ''}`,
        { variant: 'error' }
      );
    }

    setIsCreatingClaims(false);
  }, [selectedEncounters, isCreatingClaims, submitClaim, enqueueSnackbar, navigate, updateParams]);

  // Handle creating a single claim from an encounter (via row action menu)
  const handleCreateSingleClaim = useCallback(async (encounter: OrgBillingEncounterResponse) => {
    if (!encounter.patient_id || !encounter.billable) return;

    const result = await submitClaim({
      encounter_ids: [encounter.encounter_id],
      patient_id: encounter.patient_id,
    });

    if (result.success && result.data) {
      enqueueSnackbar('Claim created successfully', { variant: 'success' });
      navigate(`/billing/claim/${result.data.id}/edit`);
    } else {
      enqueueSnackbar('Failed to create claim', { variant: 'error' });
    }
  }, [submitClaim, enqueueSnackbar, navigate]);

  // Handle creating superbill from multiple encounters
  const handleCreateSuperbill = useCallback(async () => {
    if (selectedEncounters.length < 2 || isCreatingSuperbill) return;

    const billable = selectedEncounters.filter(e => e.billable && !e.has_claim && e.patient_id);
    const patientId = billable[0]?.patient_id;
    if (!patientId || !billable.every(e => e.patient_id === patientId)) {
      enqueueSnackbar('All selected encounters must be for the same patient', { variant: 'error' });
      return;
    }

    setIsCreatingSuperbill(true);

    const result = await submitClaim({
      encounter_ids: billable.map(e => e.encounter_id),
      patient_id: patientId,
    });

    if (result.success && result.data) {
      enqueueSnackbar('Superbill created successfully', { variant: 'success' });
      navigate(`/billing/claim/${result.data.id}/edit`);
      setSelectedEncounters([]);
    } else {
      enqueueSnackbar('Failed to create superbill', { variant: 'error' });
    }

    setIsCreatingSuperbill(false);
  }, [selectedEncounters, isCreatingSuperbill, submitClaim, enqueueSnackbar, navigate]);

  // Handle deleting selected claims
  const handleDeleteClaims = useCallback(async () => {
    if (selectedClaims.length === 0 || isDeletingClaims) return;

    setIsDeletingClaims(true);
    setDeleteDialogOpen(false);

    const results = await Promise.allSettled(
      selectedClaims.map(claim => deleteClaim(claim.claim_id))
    );

    const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
    const failureCount = results.length - successCount;

    if (successCount > 0) {
      enqueueSnackbar(
        `Successfully deleted ${successCount} claim${successCount > 1 ? 's' : ''}`,
        { variant: 'success' }
      );
      setSelectedClaims([]);
    }

    if (failureCount > 0) {
      enqueueSnackbar(
        `Failed to delete ${failureCount} claim${failureCount > 1 ? 's' : ''}`,
        { variant: 'error' }
      );
    }

    setIsDeletingClaims(false);
  }, [selectedClaims, isDeletingClaims, deleteClaim, enqueueSnackbar]);

  // Handle submitting selected claims
  const handleSubmitClaims = useCallback(async () => {
    if (selectedClaims.length === 0 || isSubmittingClaims) return;

    setIsSubmittingClaims(true);

    const results = await Promise.allSettled(
      selectedClaims.map(async claim => {
        const hasErrors = (claim.error_count ?? 0) > 0;
        const isNotSubmittable = ['submitted', 'processing', 'accepted', 'finalized'].includes(claim.status);

        if (hasErrors || isNotSubmittable) {
          return { success: false, skipped: true, reason: hasErrors ? 'has_errors' : 'invalid_status' };
        }

        return submitClaimToOffice(claim.claim_id);
      })
    );

    let successCount = 0;
    let blockedCount = 0;
    let skippedCount = 0;
    let failureCount = 0;

    results.forEach(result => {
      if (result.status === 'fulfilled') {
        const value = result.value;
        if ('skipped' in value && value.skipped) {
          skippedCount++;
        } else if (value.success) {
          if ('submissionBlocked' in value && value.submissionBlocked) {
            blockedCount++;
          } else {
            successCount++;
          }
        } else {
          failureCount++;
        }
      } else {
        failureCount++;
      }
    });

    if (successCount > 0) {
      enqueueSnackbar(
        `Successfully submitted ${successCount} claim${successCount > 1 ? 's' : ''}`,
        { variant: 'success' }
      );
      setSelectedClaims([]);
    }

    if (blockedCount > 0) {
      enqueueSnackbar(
        `${blockedCount} claim${blockedCount > 1 ? 's' : ''} blocked by validation errors`,
        { variant: 'warning' }
      );
    }

    if (skippedCount > 0) {
      enqueueSnackbar(
        `Skipped ${skippedCount} claim${skippedCount > 1 ? 's' : ''} (errors or already submitted)`,
        { variant: 'info' }
      );
    }

    if (failureCount > 0) {
      enqueueSnackbar(
        `Failed to submit ${failureCount} claim${failureCount > 1 ? 's' : ''}`,
        { variant: 'error' }
      );
    }

    setIsSubmittingClaims(false);
  }, [selectedClaims, isSubmittingClaims, submitClaimToOffice, enqueueSnackbar]);

  const renderTabContent = () => {
    const commonProps = {
      filters: {
        patient: params.patient,
        from: params.from,
        to: params.to,
        status: params.status,
        providers: params.providers,
        payerUuid: params.payerUuid,
        hasClaim: params.hasClaim,
        readyToBill: params.readyToBill,
      },
      sort: params.sort,
      order: params.order || ('asc' as const),
      page: params.page,
      perPage: params.perPage,
      onPageChange: handlePageChange,
      onPerPageChange: handlePerPageChange,
      onSort: handleSort,
    };

    switch (params.tab) {
      case 'claims':
        return (
          <ClaimsList
            {...commonProps}
            onSelectedClaimsChange={setSelectedClaims}
          />
        );
      case 'remittances':
        return (
          <RemittancesList
            filters={{
              from: params.from,
              to: params.to,
              payerName: params.payerName,
              paymentMethod: params.paymentMethod,
              queue: params.queue,
            }}
            sort={params.sort}
            order={params.order || 'desc'}
            page={params.page}
            perPage={params.perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onSort={handleSort}
            onSelectedRemittancesChange={setSelectedRemittances}
          />
        );
      case 'statements':
        return (
          <StatementsQueue
            page={params.page}
            perPage={params.perPage}
            sort={params.sort}
            order={params.order || 'desc'}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onSort={handleSort}
          />
        );
      case 'history':
        return (
          <StatementsHistory
            page={params.page}
            perPage={params.perPage}
            sort={params.sort}
            order={params.order || 'desc'}
            filters={{
              from: params.from,
              to: params.to,
            }}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onSort={handleSort}
          />
        );
      case 'unbilled':
      default:
        return (
          <BillingEncountersList
            {...commonProps}
            onSelectedEncountersChange={setSelectedEncounters}
            onCreateClaim={handleCreateSingleClaim}
          />
        );
    }
  };

  // Calculate which actions are available based on selection
  const billableSelections = useMemo(
    () => selectedEncounters.filter(e => e.billable && !e.has_claim && e.patient_id),
    [selectedEncounters]
  );

  const canCreateClaims = billableSelections.length > 0;
  const canCreateSuperbill = useMemo(() => {
    if (billableSelections.length < 2) return false;
    const firstPatientId = billableSelections[0].patient_id;
    return billableSelections.every(e => e.patient_id === firstPatientId);
  }, [billableSelections]);

  // Check if all selected claims can be submitted
  const hasSubmittableClaims = useMemo(() => {
    return selectedClaims.every(claim => {
      const hasErrors = (claim.error_count ?? 0) > 0;
      const isNotSubmittable = ['submitted', 'processing', 'accepted', 'finalized'].includes(claim.status);
      return !hasErrors && !isNotSubmittable;
    });
  }, [selectedClaims]);

  // Check if any selected claims have downloadable X12 files
  // Claims must have been submitted (have submitted_at) to have an X12 file in Stedi
  const downloadableClaimIds = useMemo(() => {
    return selectedClaims
      .filter(claim => !!claim.submitted_at)
      .map(claim => claim.claim_id);
  }, [selectedClaims]);

  // Check if any selected remittances have downloadable ERA files
  const downloadableRemittanceIds = useMemo(() => {
    return selectedRemittances
      .filter(r => r.source_system === 'stedi')
      .map(r => r.remittance_id);
  }, [selectedRemittances]);

  const handleDownloadClaims = useCallback(async () => {
    if (downloadableClaimIds.length === 0) return;
    await downloadBulkClaims(downloadableClaimIds);
  }, [downloadableClaimIds, downloadBulkClaims]);

  const handleDownloadEras = useCallback(async () => {
    if (downloadableRemittanceIds.length === 0) return;
    await downloadBulkEra(downloadableRemittanceIds);
  }, [downloadableRemittanceIds, downloadBulkEra]);

  const getHeaderProps = () => {
    let actions;

    if (params.tab === 'unbilled' && billableSelections.length > 0) {
      actions = (
      <Stack direction="row" spacing={1}>
        {canCreateClaims && (
          <Button
            variant="contained"
            color="primary"
            startIcon={isCreatingClaims ? <CircularProgress size={16} /> : <AddIcon />}
            onClick={handleCreateClaims}
            disabled={isCreatingClaims || isCreatingSuperbill}
            size="small"
          >
            {isCreatingClaims
              ? 'Creating...'
              : billableSelections.length === 1
              ? 'Create Claim'
              : `Create ${billableSelections.length} Claims`}
          </Button>
        )}
        {canCreateSuperbill && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={isCreatingSuperbill ? <CircularProgress size={16} /> : <DescriptionIcon />}
            onClick={handleCreateSuperbill}
            disabled={isCreatingClaims || isCreatingSuperbill}
            size="small"
          >
            {isCreatingSuperbill ? 'Creating...' : 'Create Superbill'}
          </Button>
        )}
      </Stack>
      );
    } else if (params.tab === 'claims' && selectedClaims.length > 0) {
      if (hasSubmittableClaims) {
        actions = (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isSubmittingClaims ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handleSubmitClaims}
              disabled={isSubmittingClaims || isDeletingClaims}
              size="small"
            >
              {isSubmittingClaims
                ? 'Submitting...'
                : selectedClaims.length === 1
                ? 'Submit Claim'
                : `Submit ${selectedClaims.length} Claims`}
            </Button>
            {downloadableClaimIds.length > 0 && (
              <Button
                variant="outlined"
                startIcon={isDownloadingClaims ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleDownloadClaims}
                disabled={isDownloadingClaims || isSubmittingClaims || isDeletingClaims}
                size="small"
              >
                {isDownloadingClaims ? 'Downloading...' : `Download ${downloadableClaimIds.length} 837${downloadableClaimIds.length !== 1 ? 's' : ''}`}
              </Button>
            )}
            <Button
              variant="contained"
              color="error"
              startIcon={isDeletingClaims ? <CircularProgress size={16} /> : <DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isDeletingClaims || isSubmittingClaims}
              size="small"
            >
              {isDeletingClaims
                ? 'Deleting...'
                : selectedClaims.length === 1
                ? 'Delete Claim'
                : `Delete ${selectedClaims.length} Claims`}
            </Button>
          </Stack>
        );
      } else {
        actions = (
          <Stack direction="row" spacing={1}>
            {downloadableClaimIds.length > 0 && (
              <Button
                variant="outlined"
                startIcon={isDownloadingClaims ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleDownloadClaims}
                disabled={isDownloadingClaims || isDeletingClaims}
                size="small"
              >
                {isDownloadingClaims ? 'Downloading...' : `Download ${downloadableClaimIds.length} 837${downloadableClaimIds.length !== 1 ? 's' : ''}`}
              </Button>
            )}
            <Button
              variant="contained"
              color="error"
              startIcon={isDeletingClaims ? <CircularProgress size={16} /> : <DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isDeletingClaims}
              size="small"
            >
              {isDeletingClaims
                ? 'Deleting...'
                : selectedClaims.length === 1
                ? 'Delete Claim'
                : `Delete ${selectedClaims.length} Claims`}
            </Button>
          </Stack>
        );
      }
    } else if (params.tab === 'remittances' && selectedRemittances.length > 0 && downloadableRemittanceIds.length > 0) {
      actions = (
        <Button
          variant="outlined"
          startIcon={isDownloadingEras ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={handleDownloadEras}
          disabled={isDownloadingEras}
          size="small"
        >
          {isDownloadingEras
            ? 'Downloading...'
            : downloadableRemittanceIds.length === 1
            ? 'Download ERA'
            : `Download ${downloadableRemittanceIds.length} ERAs`}
        </Button>
      );
    }

    switch (params.tab) {
      case 'claims':
        return {
          title: 'Claims',
          subtitle: 'Manage submitted claims and billing history',
          containerId: 'claims-tab',
          actions,
        };
      case 'remittances':
        return {
          title: 'Payments',
          subtitle: 'View payer remittance payments and adjudication details',
          containerId: 'remittances-tab',
          actions,
        };
      case 'statements':
        return {
          title: 'Statements',
          subtitle: 'Review and send invoices for confirmed patient balances',
          containerId: 'statements-tab',
        };
      case 'history':
        return {
          title: 'Statement History',
          subtitle: 'Completed and failed statement runs',
          containerId: 'history-tab',
        };
      case 'unbilled':
      default:
        return {
          title: 'Encounters',
          subtitle: 'Review encounters and create claims',
          containerId: 'encounters-tab',
          actions,
        };
    }
  };

  return (
    <StickyHeaderLayout {...getHeaderProps()}>
      <Container maxWidth="xl" sx={{ pb: 3 }}>
        {/* Tabs Navigation */}
        <Paper
          elevation={0}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 3,
            borderRadius: '8px 8px 0 0',
            bgcolor: 'background.paper',
          }}
        >
          <Tabs
            value={params.tab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                minHeight: 48,
                fontSize: '0.9375rem',
              },
            }}
          >
            <Tab value="unbilled" label="Encounters" />
            <Tab value="claims" label="Claims" />
            <Tab value="remittances" label="Payments" />
            <Tab value="statements" label="Statements" />
            <Tab value="history" label="History" />
          </Tabs>
        </Paper>

        {/* Filters */}
        <BillingFilters
          tab={params.tab}
          patient={params.patient}
          dateFrom={params.from}
          dateTo={params.to}
          status={params.status}
          providers={params.providers}
          hasClaim={params.hasClaim}
          readyToBill={params.readyToBill}
          payerUuid={params.payerUuid}
          payerName={params.payerName}
          paymentMethod={params.paymentMethod}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
        />

        {/* Queue Chips (encounters tab) */}
        {params.tab === 'unbilled' && (
          <EncounterQueueChips
            activeReadyToBill={params.readyToBill}
            onReadyToBillSelect={(readyToBill) => updateParams({ readyToBill }, true)}
          />
        )}

        {/* Queue Chips (claims tab) */}
        {params.tab === 'claims' && (
          <ClaimQueueChips
            activeQueue={params.status}
            onQueueSelect={(status) => updateParams({ status }, true)}
            filters={{
              patientId: params.patient,
              providerIds: params.providers,
              dateFrom: params.from,
              dateTo: params.to,
            }}
          />
        )}

        {/* Queue Chips (remittances tab) */}
        {params.tab === 'remittances' && (
          <RemittanceQueueChips
            activeQueue={params.queue}
            onQueueSelect={(queue) => updateParams({ queue }, true)}
            filters={{
              dateFrom: params.from,
              dateTo: params.to,
              payerName: params.payerName,
              paymentMethod: params.paymentMethod,
            }}
          />
        )}

        {/* Tab Content */}
        {renderTabContent()}
      </Container>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        type={selectedClaims.length === 1 ? 'claim' : `${selectedClaims.length} claims`}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteClaims}
      />
    </StickyHeaderLayout>
  );
};
